import { commonUtils } from '@sato/components';
import { AxiosError, AxiosResponse } from 'axios';

import {
  isOmaSATOApiError,
  OmaSATOApiError,
  OmaSATOApiErrorData,
} from '@models/api/identity';
import { isLocalOrTestEnv } from '@utils/featureFlags';

// TODO: Split these into different contexts. All API errors should not be in the same enum.
export enum SatoApiErrorId {
  /** Unfinished registration process */
  UNFINISHED_REGISTRATION = 'HTTP_SECURITY_ACCOUNT_PROBLEM_UNCONFIRMED',
  /** Invalid email or password */
  INVALID_CREDENTIALS = 'HTTP_SECURITY_INVALID_CREDENTIALS',
  /** Expired FTN registration cookies */
  REGISTRATION_EXPIRED = 'HTTP_SECURITY_REGISTRATIONTIME_EXPIRED',
  /** An existing OmaSATO user account found for the given email */
  EMAIL_TAKEN = 'HTTP_SECURITY_ACCOUNT_EXISTS',
  /** Email verification code invalid */
  INVALID_VERIFICATION_CODE = 'HTTP_SECURITY_INVALID_CODE',
  /** Email verification code expired */
  EXPIRED_VERIFICATION_CODE = 'HTTP_SECURITY_EXPIRED_CODE',
  /** Illegal credentials */
  UNAUTHORIZED = 'HTTP_SECURITY_UNAUTHORIZED',

  /** Parking product already rented */
  PRODUCT_ALREADY_RENTED = 'PRODUCT_ALREADY_RENTED',
  /** Parking product is locked by someone else */
  PRODUCT_ALREADY_LOCKED = 'PRODUCT_ALREADY_LOCKED',
}

/** Wrapper class for AxiosError instances that are typed accurately
 * for API error responses (i.e. expose error ID for easy matching) */
export class SatoApiError
  extends AxiosError<OmaSATOApiErrorData>
  implements OmaSATOApiError
{
  id: SatoApiErrorId;
  endpoint: string;
  statusCode: number;

  override response: AxiosResponse<OmaSATOApiErrorData>;

  constructor(id: SatoApiErrorId, endpoint: string, error: OmaSATOApiError) {
    super(
      error.message,
      error.code,
      error.config,
      error.request,
      error.response
    );

    this.id = id;
    this.endpoint = endpoint;
    this.response = error.response;
    this.statusCode = error.response.status;
  }
}

/** Utility function for throwing an ApiError error instance instead of a generic Error instance if possible */
export const handleApiError = (error: unknown): void => {
  if (!isOmaSATOApiError(error)) throw error;

  const errorId = error.response.data.errorId;
  const requestEndpoint = error.config?.url;

  if (
    commonUtils.isNullable(errorId) ||
    commonUtils.isNullable(requestEndpoint)
  ) {
    throw error;
  }

  if (
    Object.values(SatoApiErrorId).every((id) => id !== errorId) &&
    isLocalOrTestEnv
  ) {
    console.warn('Unlisted API error ID:', errorId);
  }

  throw new SatoApiError(
    // allow any string as ApiErrorId for the time being until all error IDs are exhaustively typed in frontend
    errorId as SatoApiErrorId,
    requestEndpoint,
    error
  );
};

export const isSatoApiError = (err: unknown): err is SatoApiError =>
  err instanceof SatoApiError;
